import { useQuery } from '@tanstack/react-query';
import { pnidApi } from '../api';
import { queryKeys } from '../queries/keys';

export interface PublishableInstrumentIds {
  /** The ids of the drawing's instruments the plant register would publish. */
  ids: ReadonlySet<string>;
  /** False until the drawing's first answer arrives, and after a failed request. */
  isLoaded: boolean;
  /** The last request failed. `ids` is then empty, so no stale answer stands in for it. */
  isError: boolean;
  /** Asks again, after a failure. */
  refetch: () => void;
}

/**
 * The drawing's publishable instruments, per the backend's per-asset rule (a publishable type
 * and a tag). `workingCopyUpdatedAt` is the saved drawing's `meta.updated_at`; every save moves
 * it, so the ids refetch after each save. While that refetch runs, the previous answer for the
 * same drawing stands; a failed request reports `isError` instead of any answer.
 */
export function usePublishableInstrumentIds(
  docId: string,
  workingCopyUpdatedAt: string | undefined,
): PublishableInstrumentIds {
  const query = useQuery({
    queryKey: queryKeys.publishableAssets(docId, workingCopyUpdatedAt ?? ''),
    queryFn: async ({ signal }) => {
      const assets = await pnidApi.getPublishableAssets(docId, signal);
      return new Set(assets.instruments.map((instrument) => instrument.id)) as ReadonlySet<string>;
    },
    enabled: Boolean(docId && workingCopyUpdatedAt),
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === docId ? previous : undefined,
    staleTime: Infinity,
  });
  return {
    ids: (!query.isError && query.data) || EMPTY_IDS,
    isLoaded: !query.isError && query.data !== undefined,
    isError: query.isError,
    refetch: () => { void query.refetch(); },
  };
}

const EMPTY_IDS: ReadonlySet<string> = new Set();
